import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cardTerminalApi } from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';

type Provider = '' | 'Teya' | 'Dojo';

export default function CardMachinesTab() {
  const qc = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);
  const [showTeyaSecret, setShowTeyaSecret] = useState(false);
  const [showDojoKey, setShowDojoKey] = useState(false);

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['cardTerminalConfig'],
    queryFn: async () => (await cardTerminalApi.getConfig()).data.message,
  });

  const { data: teya, isLoading: teyaLoading } = useQuery({
    queryKey: ['teyaSettings'],
    queryFn: async () => (await cardTerminalApi.getTeyaSettings()).data.message,
  });

  const { data: dojo, isLoading: dojoLoading } = useQuery({
    queryKey: ['dojoSettings'],
    queryFn: async () => (await cardTerminalApi.getDojoSettings()).data.message,
  });

  const [provider, setProvider] = useState<Provider>('');
  const [teyaForm, setTeyaForm] = useState({
    enabled: false,
    mode: 'Sandbox' as 'Sandbox' | 'Live',
    currency: 'GBP',
    store_id: '',
    terminal_id: '',
    epos_external_id: '',
    client_id: '',
    client_secret: '',
    enable_pay_by_link: true,
  });
  const [dojoForm, setDojoForm] = useState({
    enabled: false,
    currency: 'GBP',
    terminal_id: '',
    software_house_id: '',
    reseller_id: '',
    api_key: '',
    webhook_secret: '',
  });

  useEffect(() => {
    if (config?.provider === 'Teya' || config?.provider === 'Dojo') {
      setProvider(config.provider);
    } else {
      setProvider('');
    }
  }, [config?.provider]);

  useEffect(() => {
    if (!teya) return;
    setTeyaForm((f) => ({
      ...f,
      enabled: !!teya.enabled,
      mode: (teya.mode as 'Sandbox' | 'Live') || 'Sandbox',
      currency: teya.currency || 'GBP',
      store_id: teya.store_id || '',
      terminal_id: teya.terminal_id || '',
      epos_external_id: teya.epos_external_id || '',
      client_id: teya.client_id || '',
      enable_pay_by_link: teya.enable_pay_by_link !== 0,
      client_secret: '',
    }));
  }, [teya]);

  useEffect(() => {
    if (!dojo) return;
    setDojoForm((f) => ({
      ...f,
      enabled: !!dojo.enabled,
      currency: dojo.currency || 'GBP',
      terminal_id: dojo.terminal_id || '',
      software_house_id: dojo.software_house_id || '',
      reseller_id: dojo.reseller_id || '',
      api_key: '',
      webhook_secret: '',
    }));
  }, [dojo]);

  const saveProvider = useMutation({
    mutationFn: async (p: Provider) => {
      await cardTerminalApi.setProvider(p);
      // Selecting a machine should also flip that integration on for POS Card
      if (p === 'Teya') {
        await cardTerminalApi.saveTeyaSettings({
          ...teyaForm,
          enabled: 1,
          enable_pay_by_link: teyaForm.enable_pay_by_link ? 1 : 0,
          client_secret: undefined,
        });
      } else if (p === 'Dojo') {
        await cardTerminalApi.saveDojoSettings({
          ...dojoForm,
          enabled: 1,
          api_key: undefined,
          webhook_secret: undefined,
        });
      }
    },
    onSuccess: (_d, p) => {
      if (p === 'Teya') setTeyaForm((f) => ({ ...f, enabled: true }));
      if (p === 'Dojo') setDojoForm((f) => ({ ...f, enabled: true }));
      qc.invalidateQueries({ queryKey: ['cardTerminalConfig'] });
      qc.invalidateQueries({ queryKey: ['teyaSettings'] });
      qc.invalidateQueries({ queryKey: ['dojoSettings'] });
      toast.success('Active card machine updated');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const saveTeya = useMutation({
    mutationFn: async () =>
      (
        await cardTerminalApi.saveTeyaSettings({
          ...teyaForm,
          enabled: teyaForm.enabled ? 1 : 0,
          enable_pay_by_link: teyaForm.enable_pay_by_link ? 1 : 0,
          client_secret: teyaForm.client_secret || undefined,
        })
      ).data.message,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['teyaSettings'] });
      qc.invalidateQueries({ queryKey: ['cardTerminalConfig'] });
      setTeyaForm((f) => ({ ...f, client_secret: '' }));
      toast.success('Teya settings saved');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const saveDojo = useMutation({
    mutationFn: async () =>
      (
        await cardTerminalApi.saveDojoSettings({
          ...dojoForm,
          enabled: dojoForm.enabled ? 1 : 0,
          api_key: dojoForm.api_key || undefined,
          webhook_secret: dojoForm.webhook_secret || undefined,
        })
      ).data.message,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dojoSettings'] });
      qc.invalidateQueries({ queryKey: ['cardTerminalConfig'] });
      setDojoForm((f) => ({ ...f, api_key: '', webhook_secret: '' }));
      toast.success('Dojo settings saved');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const testTeya = useMutation({
    mutationFn: async () =>
      (
        await cardTerminalApi.testTeya({
          mode: teyaForm.mode,
          client_id: teyaForm.client_id || undefined,
          client_secret: teyaForm.client_secret || undefined,
        })
      ).data.message,
    onSuccess: (res) => toast.success(res?.message || 'Teya connected'),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const testDojo = useMutation({
    mutationFn: async () =>
      (
        await cardTerminalApi.testDojo({
          api_key: dojoForm.api_key || undefined,
          terminal_id: dojoForm.terminal_id || undefined,
          software_house_id: dojoForm.software_house_id || undefined,
        })
      ).data.message,
    onSuccess: (res) => toast.success(res?.message || 'Dojo connected'),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const copyText = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const status = useMemo(() => {
    if (provider === 'Teya') {
      return teya?.ready_terminal
        ? { ok: true, text: 'Teya card machine ready for POS' }
        : { ok: false, text: 'Complete Teya credentials + Store & Terminal IDs' };
    }
    if (provider === 'Dojo') {
      return dojo?.ready_terminal
        ? { ok: true, text: 'Dojo card machine ready for POS' }
        : { ok: false, text: 'Complete Dojo API key + Terminal ID' };
    }
    return { ok: false, text: 'No card machine selected — Cash & Bank still work' };
  }, [provider, teya, dojo]);

  if (configLoading || teyaLoading || dojoLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading card machine settings…
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-slate-900 text-white p-2.5">
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">Card machines</h2>
            <p className="text-sm text-slate-600 mt-1">
              Connect a Teya or Dojo <strong>card machine</strong> for POS till payments, and optionally
              Pay by Link / QR on unpaid invoices. Online shop card checkout is configured under{' '}
              <a href="/public-store?tab=card_payment" className="underline font-medium text-slate-800">
                Public Store → Card payment
              </a>{' '}
              (or Stripe).
            </p>
          </div>
        </div>

        <div
          className={`mt-4 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm ${
            status.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'
          }`}
        >
          {status.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {status.text}
        </div>
      </div>

      {/* Step 1 — pick provider */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Step 1</div>
          <h3 className="text-base font-semibold text-slate-900 mt-0.5">Which machine do you use?</h3>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {(
            [
              { id: '' as Provider, title: 'None', desc: 'Cash & Bank only' },
              { id: 'Teya' as Provider, title: 'Teya', desc: 'Teya card machine + online links' },
              { id: 'Dojo' as Provider, title: 'Dojo', desc: 'Dojo Pay at Counter terminal' },
            ] as const
          ).map((opt) => {
            const active = provider === opt.id;
            return (
              <button
                key={opt.title}
                type="button"
                onClick={() => {
                  setProvider(opt.id);
                  saveProvider.mutate(opt.id);
                }}
                className={`text-left rounded-xl border px-4 py-3 transition ${
                  active
                    ? 'border-slate-900 bg-slate-900 text-white shadow'
                    : 'border-slate-200 hover:border-slate-400 bg-white'
                }`}
              >
                <div className="font-semibold">{opt.title}</div>
                <div className={`text-xs mt-1 ${active ? 'text-slate-300' : 'text-slate-500'}`}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Teya — only when Teya is the active machine */}
      {provider === 'Teya' && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Step 2 · Teya</div>
              <h3 className="text-base font-semibold text-slate-900 mt-0.5">Your Teya account</h3>
              <p className="text-sm text-slate-500 mt-1">
                From{' '}
                <a
                  href="https://business.teya.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-800 underline inline-flex items-center gap-1"
                >
                  business.teya.com <ExternalLink className="w-3 h-3" />
                </a>{' '}
                → Integrations. Use Sandbox keys while testing.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={teyaForm.enabled}
                onChange={(e) => setTeyaForm((f) => ({ ...f, enabled: e.target.checked }))}
                className="rounded border-slate-300"
              />
              Enable Teya
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm space-y-1">
              <span className="text-slate-600">Mode</span>
              <select
                value={teyaForm.mode}
                onChange={(e) => setTeyaForm((f) => ({ ...f, mode: e.target.value as 'Sandbox' | 'Live' }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="Sandbox">Sandbox (test)</option>
                <option value="Live">Live</option>
              </select>
            </label>
            <label className="text-sm space-y-1">
              <span className="text-slate-600">Currency</span>
              <select
                value={teyaForm.currency}
                onChange={(e) => setTeyaForm((f) => ({ ...f, currency: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </label>
            <label className="text-sm space-y-1 sm:col-span-2">
              <span className="text-slate-600">Client ID</span>
              <input
                value={teyaForm.client_id}
                onChange={(e) => setTeyaForm((f) => ({ ...f, client_id: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
                placeholder="From Teya Integrations"
                autoComplete="off"
              />
            </label>
            <label className="text-sm space-y-1 sm:col-span-2">
              <span className="text-slate-600">
                Client Secret {teya?.has_client_secret ? <span className="text-emerald-600">(saved)</span> : null}
              </span>
              <div className="relative">
                <input
                  type={showTeyaSecret ? 'text' : 'password'}
                  value={teyaForm.client_secret}
                  onChange={(e) => setTeyaForm((f) => ({ ...f, client_secret: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 pr-10 font-mono text-xs"
                  placeholder={teya?.has_client_secret ? 'Leave blank to keep current secret' : 'Paste client secret'}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                  onClick={() => setShowTeyaSecret((v) => !v)}
                >
                  {showTeyaSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>
            <label className="text-sm space-y-1">
              <span className="text-slate-600">
                Store ID <span className="text-amber-600">(required for POS Card)</span>
              </span>
              <input
                value={teyaForm.store_id}
                onChange={(e) => setTeyaForm((f) => ({ ...f, store_id: e.target.value }))}
                className={`w-full rounded-lg border px-3 py-2 font-mono text-xs ${
                  !teyaForm.store_id.trim() ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'
                }`}
                placeholder="From Teya Business Portal"
              />
            </label>
            <label className="text-sm space-y-1">
              <span className="text-slate-600">
                Terminal ID <span className="text-amber-600">(required for POS Card)</span>
              </span>
              <input
                value={teyaForm.terminal_id}
                onChange={(e) => setTeyaForm((f) => ({ ...f, terminal_id: e.target.value }))}
                className={`w-full rounded-lg border px-3 py-2 font-mono text-xs ${
                  !teyaForm.terminal_id.trim() ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'
                }`}
                placeholder="Your card machine ID"
              />
            </label>
            <label className="text-sm space-y-1 sm:col-span-2">
              <span className="text-slate-600">Till / ePOS ID (optional)</span>
              <input
                value={teyaForm.epos_external_id}
                onChange={(e) => setTeyaForm((f) => ({ ...f, epos_external_id: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
                placeholder="Unique ID for this till"
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={teyaForm.enable_pay_by_link}
                onChange={(e) => setTeyaForm((f) => ({ ...f, enable_pay_by_link: e.target.checked }))}
                className="rounded border-slate-300"
              />
              Pay by Link / QR on unpaid invoices
            </label>
          </div>
          <p className="text-xs text-slate-500">
            Public store card checkout lives under{' '}
            <a href="/public-store?tab=card_payment" className="underline font-medium">
              Public Store → Card payment
            </a>
            .
          </p>

          {teya?.webhook_url ? (
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Webhook URL (paste into Teya portal)
              </div>
              <div className="flex items-center gap-2">
                <code className="text-[11px] text-slate-700 break-all flex-1">{teya.webhook_url}</code>
                <button
                  type="button"
                  onClick={() => copyText('teya-wh', teya.webhook_url)}
                  className="p-2 rounded-lg hover:bg-white border border-slate-200"
                >
                  {copied === 'teya-wh' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => saveTeya.mutate()}
              disabled={saveTeya.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
            >
              {saveTeya.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Save Teya
            </button>
            <button
              type="button"
              onClick={() => testTeya.mutate()}
              disabled={testTeya.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {testTeya.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Test connection
            </button>
          </div>
        </section>
      )}

      {/* Dojo — only when Dojo is the active machine */}
      {provider === 'Dojo' && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Step 2 · Dojo</div>
              <h3 className="text-base font-semibold text-slate-900 mt-0.5">Your Dojo account</h3>
              <p className="text-sm text-slate-500 mt-1">
                From the{' '}
                <a
                  href="https://docs.dojo.tech"
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-800 underline inline-flex items-center gap-1"
                >
                  Dojo Developer Portal <ExternalLink className="w-3 h-3" />
                </a>
                . Sandbox keys start with <code className="text-xs">sk_sandbox_</code>.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={dojoForm.enabled}
                onChange={(e) => setDojoForm((f) => ({ ...f, enabled: e.target.checked }))}
                className="rounded border-slate-300"
              />
              Enable Dojo
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm space-y-1 sm:col-span-2">
              <span className="text-slate-600">
                API Key {dojo?.has_api_key ? <span className="text-emerald-600">(saved)</span> : null}
              </span>
              <div className="relative">
                <input
                  type={showDojoKey ? 'text' : 'password'}
                  value={dojoForm.api_key}
                  onChange={(e) => setDojoForm((f) => ({ ...f, api_key: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 pr-10 font-mono text-xs"
                  placeholder={dojo?.has_api_key ? 'Leave blank to keep current key' : 'sk_sandbox_… or sk_prod_…'}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                  onClick={() => setShowDojoKey((v) => !v)}
                >
                  {showDojoKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>
            <label className="text-sm space-y-1">
              <span className="text-slate-600">Terminal ID</span>
              <input
                value={dojoForm.terminal_id}
                onChange={(e) => setDojoForm((f) => ({ ...f, terminal_id: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
              />
            </label>
            <label className="text-sm space-y-1">
              <span className="text-slate-600">Currency</span>
              <select
                value={dojoForm.currency}
                onChange={(e) => setDojoForm((f) => ({ ...f, currency: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </label>
            <label className="text-sm space-y-1">
              <span className="text-slate-600">Software House ID</span>
              <input
                value={dojoForm.software_house_id}
                onChange={(e) => setDojoForm((f) => ({ ...f, software_house_id: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
                placeholder="From Dojo / SaleDesk partner"
              />
            </label>
            <label className="text-sm space-y-1">
              <span className="text-slate-600">Reseller ID (optional)</span>
              <input
                value={dojoForm.reseller_id}
                onChange={(e) => setDojoForm((f) => ({ ...f, reseller_id: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
              />
            </label>
          </div>

          {dojo?.webhook_url ? (
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Webhook URL (paste into Dojo portal)
              </div>
              <div className="flex items-center gap-2">
                <code className="text-[11px] text-slate-700 break-all flex-1">{dojo.webhook_url}</code>
                <button
                  type="button"
                  onClick={() => copyText('dojo-wh', dojo.webhook_url)}
                  className="p-2 rounded-lg hover:bg-white border border-slate-200"
                >
                  {copied === 'dojo-wh' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => saveDojo.mutate()}
              disabled={saveDojo.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
            >
              {saveDojo.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Save Dojo
            </button>
            <button
              type="button"
              onClick={() => testDojo.mutate()}
              disabled={testDojo.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {testDojo.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Test connection
            </button>
          </div>
        </section>
      )}

      <p className="text-xs text-slate-500 px-1 pb-4">
        Tip: On the POS payment screen, choose <strong>Card</strong> to send the amount to your connected machine.
        Customers can also pay via QR / link when Teya Pay by Link is enabled.
      </p>
    </div>
  );
}
